import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  Hash,
  Loader2,
  Lock,
  SearchX,
} from "lucide-react";

import {
  fetchClub,
  fetchClubPostById,
  fetchClubPostComments,
  fetchClubPostCommentsSearch,
  addClubPostComment,
  isLikelyFileOrBlob,
  toggleClubCommentLike,
  toggleClubPostLike,
  deleteClubPost,
  buildClubPostShareUrl,
} from "../../services/clubsApi";
import {
  bodyAfterFirstLine,
  extractClubPostFromResponse,
  getOptimisticPostLikeState,
  getPostLikeStateFromToggleResponse,
  getImageAttachmentsFromPost,
  mergeClubCommentFromServer,
  mergeViewerOntoOwnClubCommentIfNeeded,
  messageForClubCommentSubmitError,
  isClubCommentUserFacingSearchMatch,
  normalizeClubCommentRow,
  resolveClubMediaUrl,
  titleFromPost,
} from "../../features/comments/utils/clubFeedPostUtils.js";
import { useAuth } from "../../context/AuthContext";
import {
  THREAD_NAV_ORIGIN_COMMUNITY,
  getCanonicalForumThreadAbsoluteUrl,
} from "../../features/posts/canonicalThreadNavigation";
import {
  FORUM_THREAD_SURFACE_CLUB,
  FORUM_THREAD_SURFACE_COMMUNITY,
  useForumThreadSurface,
} from "../../features/posts/useForumThreadSurface";
import { buildForumThreadSurfaceContract } from "../../features/posts/forumThreadSurfaceContract";
import CanonicalClubPostThreadShell from "../../features/posts/CanonicalClubPostThreadShell";
import { buildOptimisticLikeState, reconcileLikeStateFromResponse } from "../../utils/likeInteraction";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

/** Matches backend max page size for listClubPostComments. */
const CLUB_POST_COMMENTS_PAGE_SIZE = 100;

/** Lightweight poll interval for “new comments” detection on the main post thread (no WebSockets). */
const CLUB_POST_THREAD_NEW_COMMENTS_POLL_MS = 35000;

/**
 * Backend paginates main post comments by top-level (root) threads; each page includes subtrees for those
 * roots up to a global safety cap (then `subtree_truncated`).
 * hasMore when we received a full root page, subtree was capped, or there may be additional root threads.
 */
function inferRootCommentPageHasMore({ batchRootCount, pageSize }) {
  if (batchRootCount <= 0) return false;
  return batchRootCount >= pageSize;
}

/** Count comments with no parent (top-level thread roots), matching `parent_comment_id IS NULL` on the server. */
function countTopLevelClubComments(rows) {
  if (!Array.isArray(rows)) return 0;
  let n = 0;
  for (const c of rows) {
    const pid = c?.parent_comment_id;
    if (pid == null || pid === "") n += 1;
  }
  return n;
}

/** One row per comment id; keeps first occurrence (stable order / tree __order). */
function dedupeClubCommentsByIdPreserveOrder(rows) {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  const out = [];
  for (const c of rows) {
    if (!c || typeof c !== "object") continue;
    const id = Number(c.id);
    if (!Number.isFinite(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(c);
  }
  return out;
}

function pickViewerId(user) {
  const keys = [
    user?.id,
    user?.user_id,
    user?.userId,
    user?.uid,
    user?.sub,
    user?.profile?.id,
    user?.profile?.user_id,
    user?.account?.id,
    user?.account?.user_id,
    user?.account_id,
  ];
  for (const v of keys) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function isModRole(role) {
  const r = String(role || "").toLowerCase();
  return ["owner", "moderator", "mod", "admin"].includes(r);
}

function canUserDeleteClubPost({ viewerId, post, membership, club }) {
  if (viewerId == null || !post) return false;
  if (Number(post.user_id) === Number(viewerId)) return true;
  const ownerId = club?.owner_user_id ?? club?.ownerUserId;
  if (ownerId != null && Number(ownerId) === Number(viewerId)) return true;
  return isModRole(membership?.role);
}

function clubNumericId(club) {
  const v = club?.id ?? club?.club_id;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function ThreadSkeleton() {
  return (
    <div className="space-y-4 animate-pulse w-full min-w-0 max-w-full">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-6 space-y-4 w-full min-w-0 max-w-full box-border">
        <div className="flex gap-3 min-w-0">
          <div className="h-12 w-12 rounded-2xl bg-white/10 shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="h-4 w-40 bg-white/10 rounded" />
            <div className="h-3 w-28 bg-white/10 rounded" />
          </div>
        </div>
        <div className="h-6 max-w-xl w-full bg-white/10 rounded" />
        <div className="h-4 w-full bg-white/10 rounded" />
        <div className="h-4 w-[90%] bg-white/10 rounded" />
        <div className="h-48 w-full bg-white/10 rounded-xl mt-4" />
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
        <div className="h-4 w-32 bg-white/10 rounded" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-white/5 border border-white/10" />
        ))}
      </div>
    </div>
  );
}

export default function ClubPostThreadPage() {
  const { surface, slug, postIdParam, loadWithoutClubBundle } = useForumThreadSurface();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const viewerId = pickViewerId(user);

  const postIdNum = useMemo(() => {
    const n = Number(postIdParam);
    return Number.isFinite(n) ? n : NaN;
  }, [postIdParam]);

  const [club, setClub] = useState(null);
  const [membership, setMembership] = useState(null);
  const [post, setPost] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [clubPostMismatch, setClubPostMismatch] = useState(false);
  const [loadingMain, setLoadingMain] = useState(true);
  const [clubLoadWarning, setClubLoadWarning] = useState("");

  const effectiveSlug = useMemo(
    () => String(slug || post?.club_slug || "").trim(),
    [slug, post?.club_slug]
  );

  const forumSurface = useMemo(
    () =>
      buildForumThreadSurfaceContract({
        surface,
        locationThreadOrigin: location.state?.threadOrigin,
        post,
        club,
        membership,
        slug,
        effectiveSlug,
        viewerId,
      }),
    [surface, location.state?.threadOrigin, post, club, membership, slug, effectiveSlug, viewerId]
  );

  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [activeReplyTargetId, setActiveReplyTargetId] = useState(null);
  const [inlineReplyText, setInlineReplyText] = useState("");
  const [inlineSending, setInlineSending] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [shareHint, setShareHint] = useState("");
  const [actionErr, setActionErr] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [postMenuOpen, setPostMenuOpen] = useState(false);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);
  const [pendingFocusCommentId, setPendingFocusCommentId] = useState(null);
  const [commentSort, setCommentSort] = useState("new");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [commentSearchText, setCommentSearchText] = useState("");
  const [commentSearchRemote, setCommentSearchRemote] = useState({
    status: "idle",
    matches: [],
    errorMessage: "",
    searchTruncated: false,
    searchLimit: 0,
  });
  const commentsSectionRef = useRef(null);
  const postMenuRef = useRef(null);
  const sortMenuRef = useRef(null);
  const searchCommentsTriggerRef = useRef(null);
  /** Latest post for async comment loads (comment_count must not lag behind effect order). */
  const postForCommentsRef = useRef(null);
  /** Matches `commentSort` for stale in-flight load-more checks. */
  const commentSortRef = useRef(commentSort);
  /** Bumps when post or sort changes so in-flight fetches do not apply to the wrong thread. */
  const commentsRequestEpochRef = useRef(0);
  /** Bumps on each full-post search schedule; completed responses must not overwrite a newer query. */
  const commentSearchRequestEpochRef = useRef(0);
  /**
   * Cumulative top-level (root) comments loaded from the API for this post+sort — offset for the next root page.
   * Client-only rows (e.g. new reply) do not advance this.
   */
  const apiCommentRootsOffsetRef = useRef(0);
  /** Bounded list API: resume mid-root (server `list_continuation`); cleared on full reload / sort / post change. */
  const listContinuationRef = useRef(null);
  /** Prevents re-applying `?comment=` into pending focus after a successful scroll clears it (same post). */
  const appliedUrlCommentFocusRef = useRef(null);

  const [commentsHasMore, setCommentsHasMore] = useState(false);
  const [loadingMoreComments, setLoadingMoreComments] = useState(false);
  const [subtreeTruncated, setSubtreeTruncated] = useState(false);
  const [newCommentsBannerVisible, setNewCommentsBannerVisible] = useState(false);
  /** Server `comment_count` baseline for poll comparison; updated after load, refresh, dismiss, and local submits. */
  const newCommentsPollBaselineRef = useRef(0);

  const scrollToComments = useCallback(() => {
    const el = commentsSectionRef.current;
    if (!el) return;
    const reserve =
      typeof window !== "undefined"
        ? window.innerWidth >= 1024
          ? 100
          : Math.min(140, Math.max(88, Math.round(window.innerHeight * 0.08)))
        : 100;
    const top = el.getBoundingClientRect().top + window.scrollY - reserve;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!postMenuOpen) return;
    const onDoc = (e) => {
      if (postMenuRef.current && !postMenuRef.current.contains(e.target)) {
        setPostMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [postMenuOpen]);

  useEffect(() => {
    if (!sortMenuOpen) return;
    const onDoc = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setSortMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [sortMenuOpen]);

  postForCommentsRef.current = post;
  commentSortRef.current = commentSort;

  useEffect(() => {
    commentsRequestEpochRef.current += 1;
    setComments([]);
    apiCommentRootsOffsetRef.current = 0;
    setCommentsHasMore(false);
    setSubtreeTruncated(false);
    setNewCommentsBannerVisible(false);
    listContinuationRef.current = null;
    const pid = post?.id;
    if (pid != null) {
      newCommentsPollBaselineRef.current = Number(post?.comment_count) || 0;
    }
    if (!pid) return undefined;
    let cancelled = false;
    fetchClubPostById(pid)
      .then((body) => {
        if (cancelled) return;
        const p = extractClubPostFromResponse(body);
        if (!p || Number(p.id) !== Number(pid)) return;
        if (Number(postForCommentsRef.current?.id) !== Number(pid)) return;
        newCommentsPollBaselineRef.current = Number(p.comment_count) || 0;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [post?.id, commentSort]);

  useEffect(() => {
    if (!post?.id) return;
    const fromState = Number(location.state?.focusCommentId);
    if (Number.isFinite(fromState)) {
      appliedUrlCommentFocusRef.current = null;
      setPendingFocusCommentId(fromState);
      return;
    }
    const qc = searchParams.get("comment");
    const fromQuery = Number(qc);
    if (!Number.isFinite(fromQuery)) {
      appliedUrlCommentFocusRef.current = null;
      return;
    }
    const sig = `${post.id}:${qc}`;
    if (appliedUrlCommentFocusRef.current === sig) return;
    appliedUrlCommentFocusRef.current = sig;
    setPendingFocusCommentId(fromQuery);
  }, [post?.id, location.key, location.state?.focusCommentId, searchParams]);

  const shouldFocusComposer = Boolean(location.state?.focusComment);

  const firstRootContinuationPath = useMemo(() => {
    if (forumSurface.showCommunityChrome || !effectiveSlug) return null;
    const root = comments.find((c) => {
      if (!c || typeof c !== "object") return false;
      const pid = c.parent_comment_id;
      return pid == null || pid === "";
    });
    if (root?.id == null) return null;
    const s = effectiveSlug;
    const p = postIdParam != null && String(postIdParam).trim() !== "" ? String(postIdParam) : "";
    if (!s || !p) return null;
    return `/clubs/${encodeURIComponent(s)}/posts/${encodeURIComponent(p)}/comments/${encodeURIComponent(
      String(root.id)
    )}/thread`;
  }, [comments, effectiveSlug, postIdParam, forumSurface.showCommunityChrome]);

  const loadComments = useCallback(async (pid) => {
    if (!pid) return;
    const epochAtStart = commentsRequestEpochRef.current;
    setLoadingComments(true);
    setCommentsHasMore(false);
    try {
      const pack = await fetchClubPostComments(pid, {
        limit: CLUB_POST_COMMENTS_PAGE_SIZE,
        offset: 0,
        sort: commentSortRef.current,
      });
      if (epochAtStart !== commentsRequestEpochRef.current) return;
      if (Number(postForCommentsRef.current?.id) !== Number(pid)) return;

      const list = Array.isArray(pack?.comments) ? pack.comments : [];
      const batchTruncated = Boolean(pack?.subtree_truncated);
      setSubtreeTruncated(batchTruncated);
      const normalized = list.map(normalizeClubCommentRow);
      const deduped = dedupeClubCommentsByIdPreserveOrder(normalized);
      const rootsInBatch = countTopLevelClubComments(normalized);
      listContinuationRef.current = pack.list_continuation ?? null;
      if (pack.next_root_offset !== undefined && pack.next_root_offset !== null) {
        const nro = Number(pack.next_root_offset);
        apiCommentRootsOffsetRef.current = Number.isFinite(nro) ? nro : rootsInBatch;
      } else {
        apiCommentRootsOffsetRef.current = rootsInBatch;
      }
      setComments(deduped);
      setCommentsHasMore(
        inferRootCommentPageHasMore({
          batchRootCount: rootsInBatch,
          pageSize: CLUB_POST_COMMENTS_PAGE_SIZE,
        }) ||
          batchTruncated ||
          Boolean(pack.list_continuation)
      );
    } catch {
      if (epochAtStart !== commentsRequestEpochRef.current) return;
      if (Number(postForCommentsRef.current?.id) !== Number(pid)) return;
      setComments([]);
      apiCommentRootsOffsetRef.current = 0;
      listContinuationRef.current = null;
      setCommentsHasMore(false);
      setSubtreeTruncated(false);
    } finally {
      if (epochAtStart === commentsRequestEpochRef.current) {
        setLoadingComments(false);
      }
    }
  }, [commentSort]);

  const refreshNewCommentsFromPoll = useCallback(async () => {
    if (!post?.id) return;
    const postId = Number(post.id);
    if (!Number.isFinite(postId)) return;
    setActionErr("");
    try {
      const body = await fetchClubPostById(postId);
      const p = extractClubPostFromResponse(body);
      if (p && Number(p.id) === postId) {
        const remote = Number(p.comment_count) || 0;
        newCommentsPollBaselineRef.current = remote;
        setPost((prev) =>
          prev && Number(prev.id) === postId ? { ...prev, comment_count: remote } : prev
        );
      }
      await loadComments(postId);
      setNewCommentsBannerVisible(false);
    } catch (e) {
      setActionErr(e?.message || "Could not refresh comments.");
    }
  }, [post?.id, loadComments]);

  const dismissNewCommentsBanner = useCallback(async () => {
    if (!post?.id) return;
    const postId = Number(post.id);
    if (!Number.isFinite(postId)) return;
    try {
      const body = await fetchClubPostById(postId);
      const p = extractClubPostFromResponse(body);
      if (p && Number(p.id) === postId) {
        const remote = Number(p.comment_count) || 0;
        newCommentsPollBaselineRef.current = remote;
        setPost((prev) =>
          prev && Number(prev.id) === postId ? { ...prev, comment_count: remote } : prev
        );
      }
    } catch {
      /* ignore */
    }
    setNewCommentsBannerVisible(false);
  }, [post?.id]);

  useEffect(() => {
    if (
      !post?.id ||
      loadingMain ||
      forbidden ||
      notFound ||
      clubPostMismatch ||
      searchMode
    ) {
      return undefined;
    }
    const postId = Number(post.id);
    if (!Number.isFinite(postId)) return undefined;

    const tick = async () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      try {
        const body = await fetchClubPostById(postId);
        const p = extractClubPostFromResponse(body);
        if (!p || Number(p.id) !== postId) return;
        if (Number(postForCommentsRef.current?.id) !== postId) return;
        const remote = Number(p.comment_count) || 0;
        if (remote > newCommentsPollBaselineRef.current) {
          setNewCommentsBannerVisible(true);
        }
      } catch {
        /* ignore */
      }
    };

    const intervalId = window.setInterval(tick, CLUB_POST_THREAD_NEW_COMMENTS_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [post?.id, loadingMain, forbidden, notFound, clubPostMismatch, searchMode]);

  const loadMoreComments = useCallback(async () => {
    if (!post?.id || loadingMoreComments || !commentsHasMore) return;
    const epochAtStart = commentsRequestEpochRef.current;
    const postId = post.id;
    const sortAtStart = commentSortRef.current;
    setLoadingMoreComments(true);
    setActionErr("");
    try {
      const offset = apiCommentRootsOffsetRef.current;
      const pack = await fetchClubPostComments(postId, {
        limit: CLUB_POST_COMMENTS_PAGE_SIZE,
        offset,
        sort: sortAtStart,
        listContinuation: listContinuationRef.current || undefined,
      });
      if (epochAtStart !== commentsRequestEpochRef.current) return;
      if (Number(postForCommentsRef.current?.id) !== Number(postId)) return;
      if (commentSortRef.current !== sortAtStart) return;

      const list = Array.isArray(pack?.comments) ? pack.comments : [];
      const batchTruncated = Boolean(pack?.subtree_truncated);
      setSubtreeTruncated((prev) => prev || batchTruncated);
      const normalized = list.map(normalizeClubCommentRow);
      const rootsInBatch = countTopLevelClubComments(normalized);

      if (list.length === 0) {
        setCommentsHasMore(false);
        listContinuationRef.current = null;
        return;
      }

      listContinuationRef.current = pack.list_continuation ?? null;
      if (pack.next_root_offset !== undefined && pack.next_root_offset !== null) {
        const nro = Number(pack.next_root_offset);
        apiCommentRootsOffsetRef.current = Number.isFinite(nro)
          ? nro
          : apiCommentRootsOffsetRef.current + rootsInBatch;
      } else {
        apiCommentRootsOffsetRef.current += rootsInBatch;
      }

      setComments((prev) => {
        const seen = new Set(prev.map((c) => Number(c?.id)));
        const extra = normalized.filter((c) => {
          const id = Number(c?.id);
          return Number.isFinite(id) && !seen.has(id);
        });
        if (extra.length === 0) return prev;
        return [...prev, ...extra];
      });

      setCommentsHasMore(
        inferRootCommentPageHasMore({
          batchRootCount: rootsInBatch,
          pageSize: CLUB_POST_COMMENTS_PAGE_SIZE,
        }) ||
          batchTruncated ||
          Boolean(pack.list_continuation)
      );
    } catch (e) {
      if (epochAtStart !== commentsRequestEpochRef.current) return;
      if (Number(postForCommentsRef.current?.id) !== Number(postId)) return;
      setActionErr(e?.message || "Could not load more comments.");
    } finally {
      setLoadingMoreComments(false);
    }
  }, [post?.id, commentsHasMore, loadingMoreComments]);

  useEffect(() => {
    const focusId = Number(pendingFocusCommentId);
    if (!Number.isFinite(focusId)) return;
    if (loadingComments || loadingMoreComments) return;
    if (document.querySelector(`[data-comment-id="${focusId}"]`)) return;
    if (!commentsHasMore) return;
    loadMoreComments();
  }, [
    pendingFocusCommentId,
    comments,
    loadingComments,
    loadingMoreComments,
    commentsHasMore,
    loadMoreComments,
  ]);

  useEffect(() => {
    if (activeReplyTargetId == null && inlineReplyText) {
      setInlineReplyText("");
    }
  }, [activeReplyTargetId, inlineReplyText]);

  useEffect(() => {
    if (activeReplyTargetId == null) return;
    const exists = comments.some((c) => Number(c?.id) === Number(activeReplyTargetId));
    if (!exists) {
      setActiveReplyTargetId(null);
      setInlineReplyText("");
    }
  }, [comments, activeReplyTargetId]);

  useEffect(() => {
    if (loadingComments) return;
    const focusId = Number(pendingFocusCommentId);
    if (!Number.isFinite(focusId)) return;
    let attempts = 0;
    const maxAttempts = 16;
    let timer = null;
    const tryScroll = () => {
      const el = document.querySelector(`[data-comment-id="${focusId}"]`);
      if (!el) {
        attempts += 1;
        if (attempts < maxAttempts) {
          timer = window.setTimeout(tryScroll, 80);
        } else if (!commentsHasMore) {
          setPendingFocusCommentId(null);
        }
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedCommentId(focusId);
      window.setTimeout(() => setHighlightedCommentId(null), 1800);
      setPendingFocusCommentId(null);
    };
    timer = window.setTimeout(tryScroll, 60);
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [comments, loadingComments, pendingFocusCommentId, commentsHasMore]);

  useEffect(() => {
    const effectivePostId = Number.isFinite(Number(post?.id))
      ? Number(post.id)
      : Number.isFinite(postIdNum)
        ? postIdNum
        : null;
    if (!searchMode || effectivePostId == null) {
      setCommentSearchRemote({
        status: "idle",
        matches: [],
        errorMessage: "",
        searchTruncated: false,
        searchLimit: 0,
      });
      return;
    }
    const q = String(commentSearchText || "").trim();
    if (!q) {
      setCommentSearchRemote({
        status: "idle",
        matches: [],
        errorMessage: "",
        searchTruncated: false,
        searchLimit: 0,
      });
      return;
    }

    const epoch = ++commentSearchRequestEpochRef.current;
    const ac = new AbortController();
    const timer = window.setTimeout(async () => {
      if (epoch !== commentSearchRequestEpochRef.current) return;
      setCommentSearchRemote((prev) => ({
        ...prev,
        status: "loading",
        errorMessage: "",
        searchTruncated: false,
      }));
      try {
        const data = await fetchClubPostCommentsSearch(effectivePostId, {
          q,
          limit: 35,
          signal: ac.signal,
        });
        if (epoch !== commentSearchRequestEpochRef.current) return;
        const list = Array.isArray(data?.matches) ? data.matches : [];
        const matches = list
          .map((row) => normalizeClubCommentRow(row))
          .filter(Boolean)
          .filter((row) => isClubCommentUserFacingSearchMatch(row, q));
        setCommentSearchRemote({
          status: "success",
          matches,
          errorMessage: "",
          searchTruncated: Boolean(data?.search_truncated),
          searchLimit: Number(data?.search_limit) || 35,
        });
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (epoch !== commentSearchRequestEpochRef.current) return;
        const code = e?.data?.code;
        let msg = String(e?.message || "Could not search comments.");
        if (code === "CLUB_POST_NOT_FOUND") {
          msg =
            "This post was not found or is unavailable — search cannot run. Try refreshing the page.";
        } else if (code === "CLUB_POST_COMMENTS_FORBIDDEN") {
          msg = "Join this club to search comments on private posts.";
        }
        setCommentSearchRemote({
          status: "error",
          matches: [],
          errorMessage: msg,
          searchTruncated: false,
          searchLimit: 0,
        });
      }
    }, 260);

    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [searchMode, commentSearchText, post?.id, postIdNum]);

  useEffect(() => {
    if (!Number.isFinite(postIdNum)) {
      setLoadingMain(false);
      setNotFound(true);
      return;
    }

    if (!loadWithoutClubBundle && (!slug || String(slug).trim() === "")) {
      setLoadingMain(false);
      setNotFound(true);
      return;
    }

    let alive = true;
    setLoadingMain(true);
    setLoadErr("");
    setClubLoadWarning("");
    setForbidden(false);
    setNotFound(false);
    setClubPostMismatch(false);
    setClub(null);
    setMembership(null);
    setPost(null);
    setComments([]);
    apiCommentRootsOffsetRef.current = 0;
    setCommentsHasMore(false);
    appliedUrlCommentFocusRef.current = null;
    setPendingFocusCommentId(null);
    setActiveReplyTargetId(null);
    setInlineReplyText("");

    (async () => {
      let clubRes = null;
      let postResBody = null;
      let cErr = null;
      let pErr = null;

      if (loadWithoutClubBundle) {
        try {
          postResBody = await fetchClubPostById(postIdNum);
        } catch (e) {
          pErr = e;
        }
      } else {
        await Promise.all([
          fetchClub(slug)
            .then((r) => {
              clubRes = r;
            })
            .catch((e) => {
              cErr = e;
            }),
          fetchClubPostById(postIdNum)
            .then((r) => {
              postResBody = r;
            })
            .catch((e) => {
              pErr = e;
            }),
        ]);
      }

      if (!alive) return;

      if (pErr) {
        const st = pErr.status;
        if (st === 403) setForbidden(true);
        else if (st === 404) setNotFound(true);
        else setLoadErr(pErr.message || "Could not load these replies.");
        setLoadingMain(false);
        return;
      }

      const p = extractClubPostFromResponse(postResBody);
      if (!p) {
        setNotFound(true);
        setLoadingMain(false);
        return;
      }

      let c = null;
      let m = null;
      if (!loadWithoutClubBundle) {
        if (!cErr && clubRes) {
          c = clubRes?.club ?? clubRes ?? null;
          m = clubRes?.membership ?? null;
        } else if (cErr) {
          const st = cErr.status;
          setClubLoadWarning(
            st === 403
              ? "Club directory details are hidden for your account; you can still read these replies."
              : cErr.message || "Could not load club details."
          );
        }
      }

      const cid = clubNumericId(c);
      const pidClub = Number(p.club_id);

      if (!loadWithoutClubBundle && c && cid != null && Number.isFinite(pidClub) && cid !== pidClub) {
        setClubPostMismatch(true);
        setPost(p);
        setClub(c);
        setMembership(m);
        setLoadingMain(false);
        return;
      }

      setClub(c);
      setMembership(m);
      setPost(p);
      setLoadingMain(false);
    })();

    return () => {
      alive = false;
    };
  }, [slug, postIdNum, loadWithoutClubBundle]);

  /** Club-tied post on community-scoped `/community/thread/...` should use the club-scoped URL; preserve `location.state`. */
  useEffect(() => {
    if (surface !== FORUM_THREAD_SURFACE_COMMUNITY || !post?.id) return;
    const cid = Number(post.club_id);
    const cslug = String(post.club_slug ?? "").trim();
    if (!Number.isFinite(cid) || cid <= 0 || !cslug) return;
    navigate(
      `/clubs/${encodeURIComponent(cslug)}/posts/${encodeURIComponent(String(post.id))}`,
      {
        replace: true,
        state: {
          ...(typeof location.state === "object" && location.state != null ? location.state : {}),
          threadOrigin: THREAD_NAV_ORIGIN_COMMUNITY,
        },
      }
    );
  }, [surface, post?.id, post?.club_id, post?.club_slug, navigate, location.state]);

  /**
   * Hydrate club directory + membership when the URL is not already the club-scoped thread route
   * but the post row is club-associated (Community home, `/thread/:id`, etc.).
   */
  useEffect(() => {
    if (surface === FORUM_THREAD_SURFACE_CLUB || !post?.id) return;
    if (!forumSurface.postHasClub) return;
    const cslug = String(post.club_slug ?? "").trim();
    const cid = Number(post.club_id);
    if (!cslug || !Number.isFinite(cid) || cid <= 0) return;
    if (slug && String(slug).trim() === cslug) return;

    let alive = true;
    fetchClub(cslug)
      .then((res) => {
        if (!alive) return;
        setClub(res?.club ?? res ?? null);
        setMembership(res?.membership ?? null);
        setClubLoadWarning("");
      })
      .catch((e) => {
        if (!alive) return;
        const st = e?.status;
        setClubLoadWarning(
          st === 403
            ? "Club directory details are hidden for your account; you can still use this thread."
            : e?.message || "Could not load club details."
        );
      });
    return () => {
      alive = false;
    };
  }, [surface, post?.id, post?.club_id, post?.club_slug, slug, forumSurface.postHasClub]);

  useEffect(() => {
    if (!post?.id) return;
    loadComments(post.id);
  }, [post?.id, commentSort, loadComments]);

  const imageAttachments = useMemo(() => getImageAttachmentsFromPost(post), [post]);
  const attachmentUrls = useMemo(
    () => imageAttachments.map((a) => resolveClubMediaUrl(a.url)).filter(Boolean),
    [imageAttachments]
  );
  const legacyImageUrl = resolveClubMediaUrl(post?.image_url);
  const previewImages = useMemo(() => {
    if (attachmentUrls.length) return attachmentUrls;
    return legacyImageUrl ? [legacyImageUrl] : [];
  }, [attachmentUrls, legacyImageUrl]);

  const artImg = resolveClubMediaUrl(post?.artwork_image_url);
  const hasMediaOnlyBody =
    !String(post?.content || "").trim() && Boolean(previewImages.length || artImg);
  const postBodyRest = bodyAfterFirstLine(post);
  const postHeading = titleFromPost(post);

  const canDelete = canUserDeleteClubPost({ viewerId, post, membership, club });
  const likeCount = Number(post?.like_count) || 0;
  const liked = Boolean(post?.liked_by_viewer);
  const serverCommentCount = Number(post?.comment_count);
  const commentCount = Number.isFinite(serverCommentCount)
    ? Math.max(comments.length, serverCommentCount)
    : comments.length;

  const onToggleLike = async () => {
    if (!viewerId || !post?.id || !forumSurface.canParticipate) return;
    const optimistic = getOptimisticPostLikeState({ liked, likeCount });
    try {
      setLikeBusy(true);
      setActionErr("");
      setPost((prev) =>
        prev
          ? {
              ...prev,
              liked_by_viewer: optimistic.optimisticLiked,
              like_count: optimistic.optimisticCount,
            }
          : prev
      );
      const res = await toggleClubPostLike(post.id);
      const { nextLiked, nextCount } = getPostLikeStateFromToggleResponse(res, optimistic);
      setPost((prev) =>
        prev
          ? {
              ...prev,
              liked_by_viewer: nextLiked,
              like_count: nextCount,
            }
          : prev
      );
    } catch (e) {
      setPost((prev) =>
        prev
          ? {
              ...prev,
              liked_by_viewer: optimistic.prevLiked,
              like_count: optimistic.prevCount,
            }
          : prev
      );
      setActionErr(e?.message || "Could not update like.");
    } finally {
      setLikeBusy(false);
    }
  };

  const onCommentMutated = useCallback((row) => {
    const n = normalizeClubCommentRow(row);
    if (n == null || n.id == null) return;
    const idKey = String(n.id);
    const debugClubComments = process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1";
    if (debugClubComments) {
      // eslint-disable-next-line no-console
      console.info("[ClubPostThreadPage onCommentMutated]", {
        idKey,
        is_deleted: n.is_deleted,
        contentLen: String(n.content ?? "").length,
        edited_at: n.edited_at,
      });
    }
    setComments((prev) => {
      let matched = false;
      const next = prev.map((c) => {
        if (String(c?.id) !== idKey) return c;
        matched = true;
        return mergeClubCommentFromServer(c, row);
      });
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.info("[ClubPostThreadPage onCommentMutated merge]", { idKey, matched, prevLen: prev.length });
      }
      return next;
    });
  }, []);

  const onToggleCommentLike = async (comment) => {
    const commentId = Number(comment?.id);
    if (
      !viewerId ||
      !post?.id ||
      !forumSurface.canParticipate ||
      !Number.isFinite(commentId) ||
      comment?.is_deleted
    )
      return;
    const row = comments.find((c) => Number(c?.id) === commentId);
    const optimisticState = buildOptimisticLikeState({
      liked_by_viewer: row?.liked_by_viewer,
      like_count: row?.like_count,
    });
    setComments((prev) =>
      prev.map((r) =>
        Number(r?.id) === commentId
          ? {
              ...r,
              liked_by_viewer: optimisticState.optimisticLiked,
              like_count: optimisticState.optimisticCount,
            }
          : r
      )
    );
    try {
    const res = await toggleClubCommentLike(post.id, commentId);
    const canonical = reconcileLikeStateFromResponse(res, optimisticState);
    setComments((prev) =>
      prev.map((row) => {
        if (Number(row?.id) !== commentId) return row;
        return {
          ...row,
          liked_by_viewer: canonical.liked_by_viewer,
          like_count: canonical.like_count,
        };
      })
    );
    } catch (e) {
      setComments((prev) =>
        prev.map((r) =>
          Number(r?.id) === commentId
            ? {
                ...r,
                liked_by_viewer: optimisticState.prevLiked,
                like_count: optimisticState.prevCount,
              }
            : r
        )
      );
      setActionErr(e?.message || "Could not update comment like.");
    }
  };

  const onShare = async () => {
    setPostMenuOpen(false);
    const url = slug
      ? buildClubPostShareUrl(slug, post?.id)
      : post?.id != null
        ? getCanonicalForumThreadAbsoluteUrl(post)
        : "";
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: forumSurface.shareNativeTitle,
          text: "Check out these replies on AnimeHouse",
          url,
        });
        setShareHint("Shared.");
        setTimeout(() => setShareHint(""), 2500);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareHint("Link copied.");
      setTimeout(() => setShareHint(""), 2500);
    } catch {
      prompt("Copy this link:", url);
    }
  };

  const submitReply = async (payload = null) => {
    const text = String(payload?.text ?? replyText ?? "").trim();
    const rawGif = payload?.gifUrl ?? null;
    const gifUrl =
      rawGif != null && String(rawGif).trim() !== "" ? String(rawGif).trim() : null;
    const rawFile = payload?.file ?? null;
    const file = isLikelyFileOrBlob(rawFile) ? rawFile : null;
    if ((!text && !gifUrl && !file) || !viewerId || !post?.id || !forumSurface.canParticipate || sending) return;
    try {
      setSending(true);
      setActionErr("");
      const prevCount = Number(post?.comment_count) || 0;
      const c = await addClubPostComment(post.id, text, { gifUrl, file });
      const normalized = mergeViewerOntoOwnClubCommentIfNeeded(
        normalizeClubCommentRow(c),
        viewerId,
        user ? { username: user.username, profile_picture: user.profile_picture } : null
      );
      setReplyText("");
      // attachments are managed inside ThreadComposer; we clear its local draft there
      setComments((prev) => [...prev, normalized]);
      newCommentsPollBaselineRef.current = prevCount + 1;
      setPost((prev) => {
        if (!prev) return prev;
        const n = (Number(prev.comment_count) || 0) + 1;
        return { ...prev, comment_count: n };
      });
      const newId = Number(c?.id);
      if (Number.isFinite(newId)) {
        setPendingFocusCommentId(newId);
      }
    } catch (e) {
      setActionErr(messageForClubCommentSubmitError(e, "Could not post comment."));
      throw e;
    } finally {
      setSending(false);
    }
  };

  const submitInlineReply = async (payload) => {
    const comment = payload?.comment ?? payload;
    const parentId = Number(comment?.id);
    const shouldGateDeepInline = Boolean(payload?.shouldGateDeepInline);
    const text = String(payload?.text ?? inlineReplyText ?? "").trim();
    const rawGif = payload?.gifUrl ?? null;
    const gifUrl =
      rawGif != null && String(rawGif).trim() !== "" ? String(rawGif).trim() : null;
    const rawFile = payload?.file ?? null;
    const file = isLikelyFileOrBlob(rawFile) ? rawFile : null;
    if ((!text && !gifUrl && !file) || !viewerId || !post?.id || !forumSurface.canParticipate || inlineSending) return;
    if (!Number.isFinite(parentId)) return;
    try {
      setInlineSending(true);
      setActionErr("");
      const prevCount = Number(post?.comment_count) || 0;
      const created = await addClubPostComment(post.id, text, {
        parentCommentId: parentId,
        gifUrl,
        file,
      });
      setInlineReplyText("");
      setActiveReplyTargetId(null);
      if (shouldGateDeepInline && effectiveSlug) {
        navigate(
          `/clubs/${encodeURIComponent(String(effectiveSlug))}/posts/${encodeURIComponent(
            String(post.id)
          )}/comments/${encodeURIComponent(String(parentId))}/thread`,
          { state: { focusCommentId: created?.id } }
        );
        return;
      }
      const normalized = mergeViewerOntoOwnClubCommentIfNeeded(
        normalizeClubCommentRow(created),
        viewerId,
        user ? { username: user.username, profile_picture: user.profile_picture } : null
      );
      setComments((prev) => [...prev, normalized]);
      newCommentsPollBaselineRef.current = prevCount + 1;
      setPost((prev) => {
        if (!prev) return prev;
        const n = (Number(prev.comment_count) || 0) + 1;
        return { ...prev, comment_count: n };
      });
      const newId = Number(created?.id);
      if (Number.isFinite(newId)) {
        setPendingFocusCommentId(newId);
      }
    } catch (e) {
      setActionErr(messageForClubCommentSubmitError(e, "Could not post reply."));
      throw e;
    } finally {
      setInlineSending(false);
    }
  };

  const openCommentSearchHit = useCallback(
    (hit) => {
      if (!effectiveSlug) return;
      const cid = Number(hit?.id);
      const pid = Number.isFinite(Number(post?.id)) ? Number(post.id) : postIdNum;
      if (!Number.isFinite(cid) || !Number.isFinite(pid)) return;
      navigate(
        `/clubs/${encodeURIComponent(String(effectiveSlug))}/posts/${encodeURIComponent(
          String(pid)
        )}/comments/${encodeURIComponent(String(cid))}/thread`,
        { state: { focusCommentId: cid } }
      );
    },
    [navigate, post?.id, postIdNum, effectiveSlug]
  );

  const onDelete = async () => {
    setPostMenuOpen(false);
    if (!canDelete || deleteBusy || !post?.id) return;
    if (!window.confirm("Delete this post?")) return;
    try {
      setDeleteBusy(true);
      await deleteClubPost(post.id);
      navigate(forumSurface.threadBackTargetPath);
    } catch {
      /* ignore */
    } finally {
      setDeleteBusy(false);
    }
  };

  if (!Number.isFinite(postIdNum)) {
    return (
      <div className="min-h-[60vh] w-full min-w-0 max-w-full overflow-x-hidden text-white px-3 sm:px-5 pt-8 pb-20 box-border">
        <div className="mx-auto w-full max-w-[480px] min-w-0 rounded-2xl border border-white/12 bg-gradient-to-b from-white/[0.07] to-white/[0.02] backdrop-blur-md p-8 sm:p-10 text-center shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-400/25 bg-amber-500/10">
            <Hash className="h-7 w-7 text-amber-200/90" />
          </div>
          <h1 className="text-xl font-semibold text-white tracking-tight">Invalid comment link</h1>
          <p className="mt-3 text-sm text-gray-400 leading-relaxed">
            That address isn’t a valid post. Open the post from the club feed instead.
          </p>
          <Link
            to="/clubs"
            className="mt-8 inline-flex items-center justify-center rounded-xl border border-white/14 bg-white/10 px-5 py-2.5 text-sm font-medium text-white hover:bg-white/15 transition-colors"
          >
            Browse clubs
          </Link>
        </div>
      </div>
    );
  }

  if (loadingMain) {
    return (
      <div className="min-h-[60vh] w-full min-w-0 max-w-full overflow-x-hidden text-white px-3 sm:px-5 pt-4 pb-16 box-border">
        <div className="relative mx-auto w-full max-w-[1200px] min-w-0">
          <div className="pointer-events-none absolute inset-0 -z-10 opacity-90">
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/0 to-black/25" />
            <div className="absolute inset-0 [background:radial-gradient(980px_560px_at_18%_0%,rgba(255,255,255,0.06),transparent_62%)]" />
          </div>
          <div className="flex items-center gap-2 text-white/50 text-sm py-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading replies…
          </div>
          <ThreadSkeleton />
        </div>
      </div>
    );
  }

  if (forbidden || notFound || loadErr || clubPostMismatch) {
    const stateMeta = forbidden
      ? {
          icon: Lock,
          iconClass: "border-violet-400/25 bg-violet-500/10 text-violet-200",
          title: "These replies are private",
          body: "Sign in and join this club to view this post.",
        }
      : notFound
        ? {
            icon: SearchX,
            iconClass: "border-white/15 bg-white/5 text-gray-300",
            title: "Thread not found",
            body: "This post may be gone or the link is wrong.",
          }
        : clubPostMismatch
          ? {
              icon: AlertTriangle,
              iconClass: "border-amber-400/25 bg-amber-500/10 text-amber-200",
              title: "Link doesn’t match this club",
              body: "This post belongs to another club—check the URL.",
            }
          : {
              icon: AlertTriangle,
              iconClass: "border-red-400/20 bg-red-500/10 text-red-200",
              title: "Something went wrong",
              body: loadErr || "Try again in a moment.",
            };
    const StateIcon = stateMeta.icon;

    return (
      <div className="min-h-[60vh] w-full min-w-0 max-w-full overflow-x-hidden text-white px-3 sm:px-5 pt-8 pb-20 box-border">
        <div className="mx-auto w-full max-w-[520px] min-w-0 rounded-2xl border border-white/12 bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-md p-8 sm:p-10 shadow-[0_24px_80px_rgba(0,0,0,0.5)]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/10 px-4 py-2 text-sm text-gray-100 hover:bg-white/15 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Go back
          </button>

          <div className="mt-8 flex flex-col items-center text-center sm:items-start sm:text-left">
            <div
              className={cn(
                "mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border shrink-0",
                stateMeta.iconClass
              )}
            >
              <StateIcon className="h-8 w-8 opacity-95" strokeWidth={1.75} />
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">
              {stateMeta.title}
            </h1>
            <p className="mt-3 text-sm text-gray-400 leading-relaxed max-w-md">{stateMeta.body}</p>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3">
            {slug ? (
              <Link
                to={forumSurface.clubPublicPath}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-5 py-2.5 text-sm font-medium text-emerald-100 hover:bg-emerald-500/15 transition-colors"
              >
                Club page
                <ChevronRight className="h-4 w-4 opacity-80" />
              </Link>
            ) : null}
            <Link
              to="/clubs/discover"
              className="inline-flex items-center justify-center rounded-xl border border-white/12 bg-white/5 px-5 py-2.5 text-sm font-medium text-gray-200 hover:bg-white/10 transition-colors"
            >
              Discover clubs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-[40vh] w-full min-w-0 max-w-full overflow-x-hidden text-white px-3 sm:px-5 pt-10 pb-20 box-border">
        <div className="mx-auto w-full max-w-[440px] min-w-0 rounded-2xl border border-white/12 bg-white/[0.04] p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-white/35 mx-auto" />
          <p className="mt-4 text-sm text-gray-400 leading-relaxed">Refresh the page or go back to the club.</p>
        </div>
      </div>
    );
  }

  return (
    <CanonicalClubPostThreadShell
      navigate={navigate}
      forumSurface={forumSurface}
      post={post}
      club={club}
      slug={slug}
      effectiveSlug={effectiveSlug}
      postIdParam={postIdParam}
      viewerId={viewerId}
      artImg={artImg}
      postHeading={postHeading}
      postBodyRest={postBodyRest}
      hasMediaOnlyBody={hasMediaOnlyBody}
      previewImages={previewImages}
      actionErr={actionErr}
      clubLoadWarning={clubLoadWarning}
      commentsSectionRef={commentsSectionRef}
      postMenuRef={postMenuRef}
      postMenuOpen={postMenuOpen}
      setPostMenuOpen={setPostMenuOpen}
      canDelete={canDelete}
      deleteBusy={deleteBusy}
      onDelete={onDelete}
      likeBusy={likeBusy}
      onToggleLike={onToggleLike}
      liked={liked}
      likeCount={likeCount}
      scrollToComments={scrollToComments}
      commentCount={commentCount}
      onShare={onShare}
      shareHint={shareHint}
      newCommentsBannerVisible={newCommentsBannerVisible}
      searchMode={searchMode}
      refreshNewCommentsFromPoll={refreshNewCommentsFromPoll}
      dismissNewCommentsBanner={dismissNewCommentsBanner}
      replyText={replyText}
      setReplyText={setReplyText}
      sending={sending}
      submitReply={submitReply}
      commentSort={commentSort}
      setCommentSort={setCommentSort}
      sortMenuOpen={sortMenuOpen}
      setSortMenuOpen={setSortMenuOpen}
      sortMenuRef={sortMenuRef}
      commentSearchText={commentSearchText}
      setCommentSearchText={setCommentSearchText}
      setSearchMode={setSearchMode}
      commentSearchRemote={commentSearchRemote}
      setCommentSearchRemote={setCommentSearchRemote}
      searchCommentsTriggerRef={searchCommentsTriggerRef}
      shouldFocusComposer={shouldFocusComposer}
      loadingComments={loadingComments}
      comments={comments}
      commentsHasMore={commentsHasMore}
      loadingMoreComments={loadingMoreComments}
      loadMoreComments={loadMoreComments}
      activeReplyTargetId={activeReplyTargetId}
      setActiveReplyTargetId={setActiveReplyTargetId}
      inlineReplyText={inlineReplyText}
      setInlineReplyText={setInlineReplyText}
      inlineSending={inlineSending}
      submitInlineReply={submitInlineReply}
      highlightedCommentId={highlightedCommentId}
      pendingFocusCommentId={pendingFocusCommentId}
      onToggleCommentLike={onToggleCommentLike}
      onCommentMutated={onCommentMutated}
      setActionErr={setActionErr}
      openCommentSearchHit={openCommentSearchHit}
      firstRootContinuationPath={firstRootContinuationPath}
      subtreeTruncated={subtreeTruncated}
      previewOpen={previewOpen}
      setPreviewOpen={setPreviewOpen}
      previewIndex={previewIndex}
      setPreviewIndex={setPreviewIndex}
    />
  );
}
